import { ref, computed } from 'vue';

import {
  CustomFormFlowEntitiesEnum,
  CustomFormsFeatures,
  isPhoneValid,
  CustomFieldType
} from '@nova/core';

import {
  type ICustomField,
  type IFormField,
  type ITrigger,
  type IWarehouse,
  type Maybe,
  type Nullable
} from '@nova/core';

import { findLoadTypeTriggers, isTriggerEnabled } from '@/utils/loadTypeTrigger';

type AssetVisitField = {
  id: string;
  label: string;
  key: string;
  description: string;
  placeholder: string;
  required: boolean;
  type: CustomFieldType;
  testId: string;
  value: unknown;
  disabled: boolean;
};

export default function useCheckinForms(warehouse: IWarehouse, loadTypeId?: Nullable<string>) {
  const assetDetailsFields = ref([] as (ICustomField & { key: string })[]);
  const additionalAssetDetailsFields = ref([] as (ICustomField & { triggerId: string })[]);
  const currentStep = ref(0);

  const additionalAssetDetailsFieldsExist = computed(() => {
    return additionalAssetDetailsFields.value.length;
  });

  // A load type owns its check-in form outright: once it has one, that form decides, so switching
  // it off collects nothing rather than falling back to the warehouse-level form below. Only a
  // load type that was never given a form falls back — the payload carries disabled triggers
  // precisely so the two stay distinguishable.
  const additionalAssetDetailsTriggers = computed(() => {
    const loadTypeTriggers = findLoadTypeTriggers(warehouse.triggers, loadTypeId ?? null);

    if (loadTypeTriggers.length) {
      return loadTypeTriggers.filter(isTriggerEnabled);
    }

    return (warehouse.triggers || []).filter(
      (trigger: Maybe<ITrigger>) =>
        trigger?.entityName === CustomFormFlowEntitiesEnum.warehouse &&
        isTriggerEnabled(trigger) &&
        trigger.feature &&
        [CustomFormsFeatures.CheckIn, CustomFormsFeatures.CheckInAdditionalAssetDetails].includes(
          trigger.feature
        )
    );
  });

  const computedRequiredAssetVisitFields = computed(() => {
    const requiredAssetVisitFields: AssetVisitField[] = [];

    return requiredAssetVisitFields.map((field: AssetVisitField) => {
      const fieldData = {
        key: field.key,
        label: field.label,
        type: field.type,
        value: null,
        disabled: false,
        required: field.required ?? true,
        name: field.id,
        testId: field.testId
      };

      return fieldData;
    });
  });

  const areRequiredAssetDetailsFilled = computed(() => {
    return (
      assetDetailsFields.value
        .filter(field => field.required)
        .every(field => validateRequiredField(field)) &&
      additionalAssetDetailsFields.value
        .filter(field => field.required)
        .every(field => validateRequiredField(field))
    );
  });

  function validateRequiredField(field: ICustomField) {
    return (
      (field.type !== CustomFieldType.Phone && field.value) ||
      (field.type === CustomFieldType.Phone && isPhoneValid(field.value as string))
    );
  }

  function setRequiredAssetVisitFields() {
    assetDetailsFields.value = computedRequiredAssetVisitFields.value;
  }

  function prepareCustomFormsData() {
    const formData: { formFieldId: string; triggerId: string; value: ICustomField['value'] }[] = [];
    const mapFn = (cf: ICustomField & { triggerId: string }) => ({
      formFieldId: cf.name,
      triggerId: cf.triggerId,
      value: cf.value
    });

    if (additionalAssetDetailsFieldsExist.value) {
      formData.push(...additionalAssetDetailsFields.value.map(mapFn));
    }

    return formData;
  }

  function extractFieldsFromTriggers(triggers: ITrigger[]) {
    const fields: (ICustomField & { triggerId: string })[] = [];

    const getField = (
      formField: IFormField,
      triggerId: string
    ): ICustomField & { triggerId: string } => {
      return {
        label: formField.overrideLabel || formField.field.label,
        type: formField.field.type,
        value: null,
        required: formField.required,
        placeholder: formField.field.placeholder,
        description: formField.overrideDescription || formField.field.description,
        triggerId,
        name: formField.id,
        dropDownValues: formField.field.extraFields?.dropDownValues,
        maxLengthOrValue: formField.field.extraFields?.maxLengthOrValue,
        minLengthOrValue: formField.field.extraFields?.minLengthOrValue
      };
    };

    triggers.forEach(trigger => {
      if (trigger.flow?.formFrom) {
        trigger.flow.formFrom.formFields.forEach((formField: IFormField) => {
          const customField = getField(formField, trigger.id);
          fields.push(customField);
        });
      }
      if (trigger.flow?.formTo) {
        trigger.flow.formTo.formFields.forEach((formField: IFormField) => {
          const customField = getField(formField, trigger.id);
          fields.push(customField);
        });
      }
    });

    return fields;
  }

  function setCurrentStep(step: number) {
    currentStep.value = step;
  }

  function getAssetDetailsData() {
    const assetDetails: Record<string, unknown> = {};
    assetDetailsFields.value.forEach(field => {
      assetDetails[field.key] = field.value;
    });
    return assetDetails;
  }

  return {
    assetDetailsFields,
    additionalAssetDetailsFields,
    additionalAssetDetailsFieldsExist,
    additionalAssetDetailsTriggers,
    areRequiredAssetDetailsFilled,
    currentStep,
    validateRequiredField,
    setRequiredAssetVisitFields,
    extractFieldsFromTriggers,
    getAssetDetailsData,
    setCurrentStep,
    prepareCustomFormsData
  };
}
